import { Check, Loader2, Plus, X } from "lucide-react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { useId } from "react";
import { cn } from "@/lib/utils";
import type { SummaryCaptureFormProps } from "@/types/voice/summary-capture";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

/**
 * Progressive "Get a summary" form. Email first (one tap to send); name + phone
 * stay tucked away behind a quiet toggle for visitors who want a follow-up.
 * Presentational only — state + persistence live in useSummaryCapture.
 */
const SummaryCaptureForm = ({
  brandColor,
  secondaryColor,
  values,
  onChange,
  expanded,
  onToggleExpanded,
  onSubmit,
  isSubmitting,
  submitted,
  error,
  hideHeader = false,
  hideExpandToggle = false,
  compact = false,
  autoFocus = true,
  submitLabel = "Send my summary",
  footnote,
  confirmationTitle,
  confirmationBody,
  alreadyCapturedEmail,
  onChangeEmail,
}: SummaryCaptureFormProps) => {
  const fieldId = useId();
  const gradient = `linear-gradient(90deg, ${brandColor} 0%, ${secondaryColor || "#FF4DB8"} 100%)`;

  // Ask-once: a visitor who already left their email on another surface never
  // sees the form again — just a quiet confirmation of where it's going, with
  // a way to correct it. The fresh-submit celebration below still wins on the
  // surface where they just typed it in.
  if (!submitted && alreadyCapturedEmail) {
    return (
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
          style={{ background: `${brandColor}14` }}
        >
          <Check size={15} strokeWidth={2.5} style={{ color: brandColor }} />
        </div>
        <p className="min-w-0 flex-1 break-words text-[13px] leading-snug text-[#64748B]">
          Recap goes to <CopyableEmail email={alreadyCapturedEmail} className="font-medium text-[#222B45]" />
        </p>
        {onChangeEmail && (
          <button
            type="button"
            onClick={onChangeEmail}
            className="min-h-[44px] min-w-[44px] flex-shrink-0 text-[13px] font-medium text-genie-primary transition-colors hover:underline"
          >
            Change
          </button>
        )}
      </div>
    );
  }

  if (submitted) {
    return (
      <div role="status" className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-white"
          style={{ background: gradient, boxShadow: `0 10px 24px -10px ${brandColor}80` }}
        >
          <Check size={20} strokeWidth={2.75} />
        </div>
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold leading-tight text-[#222B45]">
            {confirmationTitle || "On its way"}
          </h3>
          <p className="mt-0.5 break-words text-[13px] leading-snug text-[#64748B]">
            {confirmationBody || "We'll send a recap of this conversation to your inbox shortly."}
          </p>
        </div>
      </div>
    );
  }

  const inputClass =
    "w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-genie-primary transition-shadow";

  return (
    <form onSubmit={onSubmit} className={compact ? "grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]" : "flex flex-col"}>
      {!hideHeader && (
        <div className="mb-4 flex items-center gap-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
            style={{ background: `${brandColor}14` }}
          >
            <GenieIcon name="mail" size={24} className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h3 className="text-[16px] font-semibold leading-tight text-[#222B45]">
              Get a summary
            </h3>
            <p className="mt-0.5 text-[13px] leading-snug text-[#64748B]">
              We'll email you a recap of this conversation.
            </p>
          </div>
        </div>
      )}

      <div className="min-w-0 space-y-3">
        <div>
          <label htmlFor={`${fieldId}-email`} className="sr-only">
            Email address
          </label>
          <input
            id={`${fieldId}-email`}
            type="email"
            required
            autoFocus={autoFocus}
            disabled={isSubmitting}
            aria-invalid={!!error}
            aria-describedby={error ? `${fieldId}-error` : undefined}
            inputMode="email"
            autoComplete="email"
            value={values.email}
            onChange={(e) => onChange({ email: e.target.value })}
            placeholder="you@example.com"
            className={inputClass}
          />
        </div>

        {expanded && (
          <>
            <div>
              <label htmlFor={`${fieldId}-name`} className="sr-only">
                Name
              </label>
              <input
                id={`${fieldId}-name`}
                type="text"
                autoComplete="name"
                value={values.name ?? ""}
                onChange={(e) => onChange({ name: e.target.value })}
                placeholder="Your name (optional)"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor={`${fieldId}-phone`} className="sr-only">
                Phone
              </label>
              <input
                id={`${fieldId}-phone`}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={values.phone ?? ""}
                onChange={(e) => onChange({ phone: e.target.value })}
                placeholder="Phone for follow-up (optional)"
                className={inputClass}
              />
            </div>
          </>
        )}
      </div>

      {error && <p id={`${fieldId}-error`} role="alert" className={cn("text-sm font-medium text-red-600", compact ? "order-3 sm:col-span-2" : "mt-2")}>{error}</p>}

      {!hideExpandToggle && (
        <button
          type="button"
          onClick={onToggleExpanded}
          className="mt-3 inline-flex items-center gap-1.5 self-start text-[13px] font-medium text-genie-primary transition-colors hover:underline"
        >
          {expanded ? <X size={14} strokeWidth={2.5} /> : <Plus size={14} strokeWidth={2.5} />}
          {expanded ? "Just the email is fine" : "Add name & phone for a follow-up"}
        </button>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className={cn("inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-95 disabled:cursor-wait disabled:opacity-70", compact ? "self-start" : "mt-5 shadow-lg")}
        style={{ background: gradient, ...(!compact && { boxShadow: `0 10px 28px -10px ${brandColor}80` }) }}
      >
        {isSubmitting ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            {compact ? "Saving…" : "Sending…"}
          </>
        ) : (
          submitLabel
        )}
      </button>

      {footnote !== null && (
        <p className={cn("text-center text-xs leading-snug text-gray-500", compact ? "order-4 sm:col-span-2" : "mt-3")}>
          {footnote ?? (compact ? "Optional — you can start without leaving your email." : "We'll only use this to send your summary and follow up.")}
        </p>
      )}
    </form>
  );
};

export default SummaryCaptureForm;
